import { Link } from 'react-router-dom';
import useSettings from '../hooks/useSettings';
import useWindowSize from '../hooks/useWindowSize';

const Footer = () => {
  const { getContactInfo, getActiveSocialMedia, getCompanyInfo } = useSettings();
  const { isMobile } = useWindowSize();

  // Obtener información de configuración desde la base de datos
  // NOTA: Los textos del footer (nombre de empresa y descripción) se obtienen de la base de datos
  // Para modificarlos, usar el panel de administración -> Configuración -> Información de la Empresa
  const contactInfo = getContactInfo();
  const socialMedia = getActiveSocialMedia();
  const companyInfo = getCompanyInfo();

  // Estilos inline para asegurar que el footer ocupe todo el ancho disponible
  const footerStyle = {
    width: '100%',
    backgroundColor: '#061156',
    color: 'var(--text-light)',
    padding: isMobile ? '20px 0 80px' : '60px 0 20px', // Padding inferior para bottom nav
    margin: 0,
    marginTop: 0, // NO usar 'auto' — absorbe el flex free space y colapsa el main-content en tablet/iPad
    boxSizing: 'border-box',
  };

  const containerStyle = {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: isMobile ? '0 16px' : '0 20px',
    boxSizing: 'border-box',
  };

  // Estilos para el contenido del footer
  const footerContentStyle = {
    display: isMobile ? 'block' : 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr',
    gap: isMobile ? '20px' : '40px',
    marginBottom: isMobile ? '20px' : '40px',
  };

  const footerSectionStyle = {
    marginBottom: isMobile ? '20px' : '0',
  };

  const footerTitleStyle = {
    fontSize: isMobile ? '16px' : '18px',
    fontWeight: '600',
    marginBottom: isMobile ? '8px' : '12px',
    color: '#ffffff',
  };

  const footerListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  };

  const footerLinkStyle = {
    color: 'rgba(255, 255, 255, 0.8)',
    textDecoration: 'none',
    fontSize: isMobile ? '14px' : '15px',
    lineHeight: isMobile ? '1.6' : '1.8',
    transition: 'color 0.3s ease',
  };

  const socialIconsStyle = {
    display: 'flex',
    gap: isMobile ? '12px' : '16px',
    marginTop: '8px',
  };

  const socialLinkStyle = {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: isMobile ? '20px' : '24px',
    transition: 'color 0.3s ease',
  };

  const copyrightStyle = {
    textAlign: 'center',
    paddingTop: isMobile ? '15px' : '20px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: isMobile ? '12px' : '14px',
  };

  return (
    <footer style={footerStyle}>
      <div className="container" style={containerStyle}>
        <div className="footer-content" style={footerContentStyle}>
          {/* Logo y descripción */}
          <div className="footer-logo" style={footerSectionStyle}>
            <h3 style={{
              ...footerTitleStyle,
              fontSize: isMobile ? '18px' : '20px',
              marginBottom: isMobile ? '8px' : '12px'
            }}>
              {companyInfo?.name || 'Liga Prokids'}
            </h3>
            <p style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: isMobile ? '14px' : '15px',
              lineHeight: '1.5',
              margin: isMobile ? '0 0 16px 0' : '0 0 20px 0',
            }}>
              {companyInfo?.description || 'Liga Prokids - Desarrollando jóvenes talentos'}
            </p>

            {/* Enlaces legales compactos */}
            {!isMobile && (
              <div style={{ marginTop: '16px' }}>
                <h4 style={footerTitleStyle}>Legal</h4>
                <ul style={footerListStyle}>
                  <li style={{ marginBottom: '4px' }}>
                    <Link to="/aviso-legal" style={footerLinkStyle}>Aviso Legal</Link>
                  </li>
                  <li style={{ marginBottom: '4px' }}>
                    <Link to="/privacy" style={footerLinkStyle}>Protección de Datos</Link>
                  </li>
                  <li style={{ marginBottom: '4px' }}>
                    <Link to="/terms" style={footerLinkStyle}>Términos</Link>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Enlaces rápidos - Solo en desktop */}
          {!isMobile && (
            <div className="footer-links" style={footerSectionStyle}>
              <h4 style={footerTitleStyle}>Enlaces</h4>
              <ul style={footerListStyle}>
                <li style={{ marginBottom: '4px' }}>
                  <Link to="/summer-edition" style={footerLinkStyle}>Summer Edition</Link>
                </li>
                <li style={{ marginBottom: '4px' }}>
                  <Link to="/liga-prokids" style={footerLinkStyle}>Liga Prokids</Link>
                </li>
                <li style={{ marginBottom: '4px' }}>
                  <Link to="/noticias" style={footerLinkStyle}>Noticias</Link>
                </li>
                <li style={{ marginBottom: '4px' }}>
                  <Link to="/about" style={footerLinkStyle}>Sobre Nosotros</Link>
                </li>
                <li style={{ marginBottom: '4px' }}>
                  <Link to="/contact" style={footerLinkStyle}>Contacto</Link>
                </li>
                <li style={{ marginBottom: '4px' }}>
                  <Link to="/preguntas-frecuentes" style={footerLinkStyle}>FAQ</Link>
                </li>
              </ul>
            </div>
          )}

          {/* Contacto compacto */}
          <div className="footer-contact" style={footerSectionStyle}>
            <h4 style={footerTitleStyle}>Contacto</h4>
            {contactInfo ? (
              <div style={{ fontSize: isMobile ? '13px' : '14px', lineHeight: '1.6' }}>
                {contactInfo.phone && (
                  <p style={{
                    margin: '0 0 4px 0',
                    color: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span className="material-icons" style={{ fontSize: '16px' }}>phone</span>
                    {contactInfo.phone}
                  </p>
                )}
                {contactInfo.email && (
                  <p style={{
                    margin: '0',
                    color: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span className="material-icons" style={{ fontSize: '16px' }}>email</span>
                    {contactInfo.email}
                  </p>
                )}
              </div>
            ) : (
              <div style={{ fontSize: isMobile ? '13px' : '14px', lineHeight: '1.6' }}>
                <p style={{
                  margin: '0 0 4px 0',
                  color: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span className="material-icons" style={{ fontSize: '16px' }}>phone</span>
                  686254944
                </p>
                <p style={{
                  margin: '0',
                  color: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span className="material-icons" style={{ fontSize: '16px' }}>email</span>
                  contactoligaprokids@gmail.com
                </p>
              </div>
            )}
          </div>

          {/* Redes sociales */}
          <div className="footer-social" style={footerSectionStyle}>
            <h4 style={footerTitleStyle}>Síguenos</h4>
            <div className="social-icons" style={socialIconsStyle}>
              {socialMedia && socialMedia.length > 0 ? (
                socialMedia.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Síguenos en ${social.platform}`}
                    style={socialLinkStyle}
                    onMouseOver={(e) => e.target.style.color = '#ffffff'}
                    onMouseOut={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.8)'}
                  >
                    <span className="material-icons" style={{ fontSize: 'inherit' }}>
                      {social.icon}
                    </span>
                  </a>
                ))
              ) : (
                <>
                  <a
                    href="https://www.facebook.com/prokids.experience"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={socialLinkStyle}
                    onMouseOver={(e) => e.target.style.color = '#ffffff'}
                    onMouseOut={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.8)'}
                  >
                    <span className="material-icons" style={{ fontSize: 'inherit' }}>facebook</span>
                  </a>
                  <a
                    href="https://www.instagram.com/prokids.experience?igsh=MXFwOGx2azU3bWwxYg=="
                    target="_blank"
                    rel="noopener noreferrer"
                    style={socialLinkStyle}
                    onMouseOver={(e) => e.target.style.color = '#ffffff'}
                    onMouseOut={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.8)'}
                  >
                    <span className="material-icons" style={{ fontSize: 'inherit' }}>photo_camera</span>
                  </a>
                  <a
                    href="https://www.youtube.com/@ProKidsExperience"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={socialLinkStyle}
                    onMouseOver={(e) => e.target.style.color = '#ffffff'}
                    onMouseOut={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.8)'}
                  >
                    <span className="material-icons" style={{ fontSize: 'inherit' }}>smart_display</span>
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="footer-bottom" style={copyrightStyle}>
          <p style={{ margin: 0 }}>
            &copy; {new Date().getFullYear()} {companyInfo?.name || 'Liga Prokids'}.
            {isMobile ? ' Todos los derechos reservados.' : ' Todos los derechos reservados.'}
          </p>

          {/* Enlaces legales en móvil - compactos */}
          {isMobile && (
            <div style={{
              marginTop: '12px',
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <Link to="/privacy" style={{
                ...footerLinkStyle,
                fontSize: '12px',
                textDecoration: 'underline'
              }}>
                Protección de Datos
              </Link>
              <Link to="/terms" style={{
                ...footerLinkStyle,
                fontSize: '12px',
                textDecoration: 'underline'
              }}>
                Términos
              </Link>
              <Link to="/aviso-legal" style={{
                ...footerLinkStyle,
                fontSize: '12px',
                textDecoration: 'underline'
              }}>
                Legal
              </Link>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
